import {
  MultiStepForm,
  type StepConfig,
} from '@/components/ui/multi-step-form';
import { useGetSession } from '@/features/auth/api/get-session';
// The per-item useCreateService/useCreateLocation/useCreatePractitioner
// hooks used to be called from a loop here. They moved into a single
// applyWebsiteAnalysis call (via useApplySnapshot) so packages/variants/
// memberships land too.
// TODO: Re-enable when integrations are built
// import { useInitiateStripeConnect } from '@/features/integrations/api';
import { useCreateOrganization } from '@/features/organization/api/create-organization';
import { useListOrganizations } from '@/features/organization/api/list-organizations';
import { useUploadImage } from '@/features/upload';
import {
  useAnalyzeWebsite,
  useApplySnapshot,
} from '@/features/website-analysis';
import type {
  ApplyAnalysisResponse,
  WebsiteAnalysisSnapshot,
} from '@/features/website-analysis/api/types';
import { logError } from '@/lib/log-error';
import { refetchSession } from '@/lib/session';
import { useResolvedRoutes } from '@/lib/use-routes';
import { apiClient } from '@borradh-workspace/api-client';
import { businessTypeValues } from '@borradh-workspace/api-client/types';
import { servicePriceTypeValues } from '@borradh-workspace/labels';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { applySnapshotToForm } from './apply-snapshot-to-form';
import { buildSnapshotFromForm } from './build-snapshot-from-form';
import { Step1BusinessInfo } from './tabs/step-1-business-info';
import { Step2BusinessType } from './tabs/step-2-business-type';
import { Step3WebsiteAnalysis } from './tabs/step-3-website-analysis';
import { Step5Services } from './tabs/step-5-services';
import { Step6Locations } from './tabs/step-6-locations';
// TODO: Re-enable when integrations are built
// import { Step6StripeConnect } from './tabs/step-6-stripe-connect';
// import { Step7Deposits } from './tabs/step-7-deposits';
import { Step7OpeningHours } from './tabs/step-7-opening-hours';
import { Step8Credibility } from './tabs/step-8-credibility';
import { StepBookingLinks } from './tabs/step-booking-links';
// TODO: Re-enable when integrations are built
// import { StepDepositSystemSelect } from './tabs/step-deposit-system-select';
import { StepDoYouProvideServices } from './tabs/step-do-you-provide-services';
import { StepPractitioners } from './tabs/step-practitioners';

// SessionStorage key for persisting form state across OAuth redirects
const ONBOARDING_FORM_STATE_KEY = 'borradh_onboarding_form_state';

interface SavedOnboardingState {
  formValues: Record<string, unknown>;
  organizationId: string;
}

// Location schema
const locationSchema = z.object({
  phone: z.string().trim().max(50).optional(),
  id: z.string(),
  name: z.string().optional(),
  addressLine1: z.string().min(1, 'Address is required'),
  addressLine2: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  county: z.string().optional(),
  postalCode: z.string().optional(),
  country: z.string().min(1, 'Country is required'),
  isPrimary: z.boolean(),
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
});

// Onboarding schema - redesigned with booking/payments flow
const onboardingSchema = z.object({
  // Step 1: Business Info
  companyLogo: z.instanceof(File).optional().nullable(),
  companyName: z.string().min(1, 'Company name is required'),
  businessType: z.enum(businessTypeValues, {
    message: 'Please select a business type',
  }),

  // Step 2: Website Scanner (optional)
  websiteUrl: z
    .string()
    .transform((v) => {
      if (!v) return v;
      const trimmed = v.trim();
      if (trimmed && !/^https?:\/\//i.test(trimmed))
        return `https://${trimmed}`;
      return trimmed;
    })
    .pipe(z.string().url('Invalid website URL'))
    .optional()
    .or(z.literal('')),
  facebookPageUrl: z
    .string()
    .transform((v) => {
      if (!v) return v;
      const trimmed = v.trim();
      if (trimmed && !/^https?:\/\//i.test(trimmed))
        return `https://${trimmed}`;
      return trimmed;
    })
    .pipe(z.string().url('Invalid Facebook URL'))
    .optional()
    .or(z.literal('')),
  bookingSystemUrl: z
    .string()
    .transform((v) => {
      if (!v) return v;
      const trimmed = v.trim();
      if (trimmed && !/^https?:\/\//i.test(trimmed))
        return `https://${trimmed}`;
      return trimmed;
    })
    .pipe(z.string().url('Invalid booking system URL'))
    .optional()
    .or(z.literal('')),

  // Services (populated by website analysis, created during org creation)
  services: z.array(z.string()).default([]),
  serviceCategories: z.record(z.string(), z.string()).default({}),
  // Brand tone & ICP (populated by website analysis, handled by backend)
  targetAudienceDescription: z.string().optional().default(''),
  brandVoice: z.array(z.string()).optional().default([]),
  suggestedCredibilityLines: z.array(z.string()).optional().default([]),

  // Locations
  locations: z
    .array(locationSchema, {
      message: 'Add at least one location',
    })
    .min(1, 'Add at least one location'),

  // Practitioners / Team members (optional — step skipped for now)
  practitioners: z
    .array(
      z.object({
        id: z.string(),
        name: z.string().min(1),
        email: z.string().email(),
        title: z.string().optional(),
        phone: z.string().optional(),
      })
    )
    .default([]),

  // Opening Hours
  businessHours: z.record(
    z.string(),
    z.object({
      from: z.number().int().min(0).max(1440),
      to: z.number().int().min(0).max(1440),
    })
  ),

  // Credibility Line
  credibilityLine: z.string().min(1, 'Choose or write a credibility line'),

  // Booking system choice
  bookingSystemChoice: z
    .enum(['borradh', 'calendly', 'timely', 'fresha', 'phorest', 'not_listed'])
    .default('borradh'),

  // Account-level booking & deposit links
  bookingLink: z.string().url().optional().or(z.literal('')),
  depositLink: z.string().url().optional().or(z.literal('')),

  // Deposit system choice
  depositSystemChoice: z.enum(['stripe', 'link', 'none']).default('none'),

  // Per-service details (keyed by service name)
  serviceDetails: z
    .record(
      z.string(),
      z.object({
        appointmentDuration: z.number().int().min(5).max(480).optional(),
        // The scanned service description, carried to the service row.
        description: z.string().max(5000).optional(),
        requiresDeposit: z.boolean().default(false),
        depositAmountCents: z.number().int().min(100).optional(),
        // Structured price, not freeform text: the analyzer returns a
        // classified anchor (priceType + priceAmount) alongside its prose, and
        // that is what the service model stores. The old `pricingDescription`
        // carried the analyzer's full multi-point blurb, which routinely blew
        // its length cap and failed the wizard's final validation.
        priceType: z.enum(servicePriceTypeValues).optional(),
        priceCents: z.number().int().min(0).optional(),
      })
    )
    .default({}),

  // Stripe Connect
  stripeConnected: z.boolean().default(false),

  // Colors (populated by website analysis)
  primaryColor: z.string().optional().default('#7c3aed'),
  secondaryColor: z.string().optional().default('#f5f5f5'),

  // Whether the org owner personally provides services
  ownerProvidesServices: z.boolean().default(false),
});

export type OnboardingFormData = z.infer<typeof onboardingSchema>;

// Step schemas for validation
const step1Schema = onboardingSchema.pick({ companyName: true });
const step1bSchema = onboardingSchema.pick({ businessType: true });
const step2Schema = z.object({});
const step6Schema_locations = onboardingSchema.pick({ locations: true });
const step4Schema = z.object({
  businessHours: z
    .record(z.string(), z.object({ from: z.number(), to: z.number() }))
    .refine(
      (obj) => Object.keys(obj).length > 0,
      'Set at least one day of opening hours'
    ),
});
const step5Schema = onboardingSchema.pick({ credibilityLine: true });
const stepPractitionersSchema = onboardingSchema.pick({ practitioners: true });
// Booking links / payment links / Stripe / deposits / calendar steps have no required form fields - passthrough
const bookingLinksStepSchema = z.object({});
// TODO: Re-enable when integrations are built
// const depositSystemSelectStepSchema = z.object({});
// const stripeConnectStepSchema = z.object({});
// const depositsStepSchema = z.object({});
const ownerProvidesServicesSchema = z.object({});

const BASE_DEFAULT_VALUES: OnboardingFormData = {
  companyLogo: null,
  companyName: '',
  businessType: '' as OnboardingFormData['businessType'],
  locations: [],
  practitioners: [],
  websiteUrl: '',
  facebookPageUrl: '',
  bookingSystemUrl: '',
  services: [],
  serviceCategories: {},
  targetAudienceDescription: '',
  brandVoice: [],
  suggestedCredibilityLines: [],
  businessHours: {
    '1': { from: 540, to: 1020 }, // Mon 9am-5pm
    '2': { from: 540, to: 1020 },
    '3': { from: 540, to: 1020 },
    '4': { from: 540, to: 1020 },
    '5': { from: 540, to: 1020 },
  },
  bookingSystemChoice: 'borradh' as const,
  bookingLink: '',
  depositLink: '',
  depositSystemChoice: 'none' as const,
  serviceDetails: {},
  credibilityLine: '',
  stripeConnected: false,
  primaryColor: '#7c3aed',
  secondaryColor: '#f5f5f5',
  ownerProvidesServices: false,
};

export function OnboardingForm() {
  const { user, isLoading: isSessionLoading } = useGetSession();
  const { data: organizations, isLoading: isOrgsLoading } =
    useListOrganizations();
  const navigate = useNavigate();
  // Dashboard routes are location-scoped since #927; the resolved routes hook
  // is what turns a bare 'home' into this org's branch path.
  const routes = useResolvedRoutes();

  // Detect OAuth return (Google Calendar or Stripe) and restore saved form state
  const [oauthReturn] = useState<
    | (SavedOnboardingState & {
        source?: 'calendar' | 'stripe';
      })
    | null
  >(() => {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);

    const isCalendarReturn =
      params.get('integration') === 'calendar' &&
      params.get('status') === 'connected';
    const isStripeReturn =
      params.get('stripe') === 'connected' ||
      (params.get('integration') === 'stripe' &&
        params.get('status') === 'connected');

    if (!isCalendarReturn && !isStripeReturn) return null;

    const saved = sessionStorage.getItem(ONBOARDING_FORM_STATE_KEY);
    if (!saved) return null;

    try {
      const parsed = JSON.parse(saved) as SavedOnboardingState;
      sessionStorage.removeItem(ONBOARDING_FORM_STATE_KEY);
      return {
        ...parsed,
        source: isStripeReturn ? 'stripe' : 'calendar',
      };
    } catch {
      return null;
    }
  });

  // Track created organization (ref for idempotency, no re-renders needed)
  const organizationIdRef = useRef<string | null>(
    oauthReturn?.organizationId ?? null
  );
  // Whether the wizard's catalog (services, locations, team) has been written.
  // Tracked apart from the org: the org can exist while this write failed, and
  // Continue must then retry only the write. An OAuth return comes back after
  // both had already happened.
  const snapshotAppliedRef = useRef(!!oauthReturn?.organizationId);
  // The logo URL the org was created with — the catalog write needs it too,
  // and a retry no longer re-uploads.
  const companyLogoUrlRef = useRef<string | undefined>(undefined);
  // A write that timed out on our side may still be running on the server. A
  // retry waits for that request rather than racing a second one against it.
  const applyInFlightRef = useRef<Promise<ApplyAnalysisResponse> | null>(null);

  // TODO: Re-enable when integrations are built
  // const [stripeConnected, setStripeConnected] = useState(
  //   oauthReturn?.source === 'stripe' ||
  //     !!oauthReturn?.formValues?.stripeConnected
  // );
  // const [bookingChoice, setBookingChoice] = useState<string>(
  //   (oauthReturn?.formValues?.bookingSystemChoice as string) || 'borradh'
  // );
  // const [depositChoice, setDepositChoice] = useState<string>(
  //   (oauthReturn?.formValues?.depositSystemChoice as string) || 'none'
  // );

  // Prevent organizations-exist redirect when org was just created or returning from OAuth
  const hasCompletedOnboardingRef = useRef(!!oauthReturn);
  const formDataRef = useRef<OnboardingFormData | null>(null);

  // Upload hook for company logo
  const { uploadAsync: uploadImage } = useUploadImage({ purpose: 'profile' });

  const { executeAsync: createOrganizationAsync } = useCreateOrganization({
    showSuccessToast: false,
    onError: (error) => {
      toast.error(error.message || 'Failed to create organization');
    },
  });

  // Website analysis hook
  const { analyzeWebsiteAsync, isAnalyzing } = useAnalyzeWebsite();

  // The last snapshot the wizard saw (from scanner OR operator upload). Held
  // in a ref because form fields don't carry `packages` / `variants` /
  // `memberships` — those flow straight from this snapshot into the
  // apply-snapshot call at submit time. `null` when neither path ran.
  const analysisSnapshotRef = useRef<WebsiteAnalysisSnapshot | null>(null);

  // Apply-snapshot hook: replaces the three per-item loops the wizard used to
  // run at org-creation time (createService/Location/Practitioner). Those
  // loops silently dropped packages, variants and memberships.
  const { applySnapshotAsync } = useApplySnapshot();

  // TODO: Re-enable when integrations are built
  // const { initiateStripeConnect, isInitiating: isConnectingStripe } =
  //   useInitiateStripeConnect({ returnTo: '/onboarding' });

  // Clean URL params on OAuth return
  useEffect(() => {
    if (oauthReturn) {
      window.history.replaceState({}, '', '/onboarding');
    }
  }, [oauthReturn]);

  // Redirect to sign-in if not authenticated
  useEffect(() => {
    if (!isSessionLoading && !user?.id) {
      const timer = setTimeout(() => {
        void navigate({ to: '/sign-in' });
      }, 500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isSessionLoading, user?.id, navigate]);

  // Redirect if already onboarded (has organizations)
  useEffect(() => {
    const handleExistingOrganizations = async () => {
      if (
        !hasCompletedOnboardingRef.current &&
        !isOrgsLoading &&
        organizations.length > 0
      ) {
        // Same reasoning as handleSubmit: someone who already has an org has
        // already been through this, and lands in the product rather than on a
        // plan picker.
        void navigate({ to: routes.home });
      }
    };
    // Only awaits the already-safe `navigate()` call above, which never rejects.
    void handleExistingOrganizations();
  }, [isOrgsLoading, organizations, navigate, routes.home]);

  // Still loading or redirecting
  if (isSessionLoading || isOrgsLoading || !user?.id) {
    return <div>Loading...</div>;
  }

  // If user has organizations and not completing onboarding, show nothing while redirecting
  if (!hasCompletedOnboardingRef.current && organizations.length > 0) {
    return <div>Setting up your account...</div>;
  }

  // TODO: Re-enable when integrations are built (used for OAuth redirect state persistence)
  // const saveFormState = (form: UseFormReturn<OnboardingFormData>) => {
  //   const values = form.getValues();
  //   const { companyLogo: _logo, ...serializableValues } = values;
  //   const state: SavedOnboardingState = {
  //     formValues: serializableValues,
  //     organizationId: organizationIdRef.current ?? '',
  //   };
  //   sessionStorage.setItem(ONBOARDING_FORM_STATE_KEY, JSON.stringify(state));
  // };

  /**
   * Race a promise against a timeout. Resolves to the result or undefined on timeout.
   */
  const withTimeout = <T,>(
    promise: Promise<T>,
    ms: number
  ): Promise<T | undefined> =>
    Promise.race([
      promise,
      new Promise<undefined>((resolve) =>
        setTimeout(() => resolve(undefined), ms)
      ),
    ]);

  /**
   * Create the organization, then write the wizard's catalog into it.
   * Called via onBeforeContinue on the credibility step.
   *
   * Two writes, tracked separately, and BOTH must land before the wizard moves
   * on. It used to return true whatever happened to the second: on 2026-10-05
   * every catalog request was rejected (a stale branch header from the previous
   * account in the tab), the wizard toasted "some setup steps did not finish"
   * and carried on, and the customer was dropped into a business with no
   * services and no locations. Now a failed write keeps them on this step with
   * the reason, and Continue retries only what is missing. Re-applying is safe:
   * the writer plans against what already exists, so nothing lands twice.
   */
  const createOrganization = async (
    form: UseFormReturn<OnboardingFormData>
  ): Promise<boolean> => {
    if (organizationIdRef.current && snapshotAppliedRef.current) return true;

    // Prevent the organizations-exist redirect while creating, and while a
    // failed catalog write is being retried.
    hasCompletedOnboardingRef.current = true;

    const data = form.getValues();

    // Another tab can replace the browser's session while this form is open.
    // Recheck before creating the org or applying its catalog.
    try {
      const currentSession = await refetchSession();
      if (currentSession.user?.id !== user.id) {
        hasCompletedOnboardingRef.current = false;
        toast.error('The signed-in account changed. Reload and try again.');
        return false;
      }
    } catch {
      hasCompletedOnboardingRef.current = false;
      toast.error('Could not verify the signed-in account. Please try again.');
      return false;
    }

    if (!organizationIdRef.current) {
      const created = await createOrganizationOnce(data);
      if (!created) return false;
    }

    try {
      return await applyCatalog(data);
    } catch (error) {
      logError('onboarding.applySnapshot', error, {
        extra: { organizationId: organizationIdRef.current },
      });
      const reason = error instanceof Error ? error.message : '';
      toast.error(
        `We couldn't save your services and locations${reason ? ` (${reason})` : ''}. Please try again.`
      );
      return false;
    }
  };

  /** Upload the logo and create the org. Returns false (and says why) on failure. */
  const createOrganizationOnce = async (
    data: OnboardingFormData
  ): Promise<boolean> => {
    try {
      // ── Upload logo (optional, best-effort) ──────────────────────────
      let companyLogoUrl: string | undefined;
      if (data.companyLogo instanceof File) {
        try {
          const result = await withTimeout(
            uploadImage(data.companyLogo),
            10_000
          );
          companyLogoUrl = result?.url;
        } catch {
          console.warn('Logo upload failed, continuing without logo');
        }
      }

      // ── Create organization (critical) ───────────────────────────────
      const org = await createOrganizationAsync({
        name: data.companyName,
        ...(companyLogoUrl && { logo: companyLogoUrl }),
        businessType: data.businessType,
        websiteUrl: data.websiteUrl || undefined,
        facebookPageUrl: data.facebookPageUrl || undefined,
        brandVoice: data.brandVoice,
        targetAudienceDescription: data.targetAudienceDescription,
        credibilityLine: data.credibilityLine,
        primaryColor: data.primaryColor,
        secondaryColor: data.secondaryColor,
        businessHours:
          data.businessHours && Object.keys(data.businessHours).length > 0
            ? data.businessHours
            : undefined,
        defaultBookingLink: data.bookingLink || undefined,
        // `depositLink` is no longer sent — deposits are collected in the
        // booking flow, not by pasting a static payment URL. The step that
        // used to capture one (StepDepositSystemSelect) is already out of the
        // flow, so nothing collects it either.
        primaryCalendarType: (() => {
          const choice = data.bookingSystemChoice;
          if (choice === 'fresha') return 'fresha';
          if (choice === 'phorest') return 'phorest';
          return 'borradh';
        })(),
      });

      if (!org?.id) {
        hasCompletedOnboardingRef.current = false;
        toast.error('Failed to create organization. Please try again.');
        return false;
      }

      // Store org id immediately so retries skip org creation
      organizationIdRef.current = org.id;
      companyLogoUrlRef.current = companyLogoUrl;
      return true;
    } catch (error) {
      hasCompletedOnboardingRef.current = false;
      logError('onboarding.createOrganization', error);
      toast.error('Failed to complete setup. Please try again.');
      return false;
    }
  };

  /**
   * Apply the analysis snapshot (services + variants + packages + memberships
   * + locations + practitioners) to the new org in one write, then send the
   * team invitations. Returns false — keeping the wizard on this step — when
   * the write timed out, and throws when it was rejected.
   */
  const applyCatalog = async (data: OnboardingFormData): Promise<boolean> => {
    const organizationId = organizationIdRef.current;
    if (!organizationId) return false;

    // Snapshot precedence:
    //   1. Form values win for fields the wizard shows (services, brand,
    //      hours, locations, practitioners, URLs) — the operator may have
    //      edited them mid-wizard.
    //   2. Ref values fill fields the wizard has no UI for (packages,
    //      memberships, per-service variants, businessDescription).
    // See buildSnapshotFromForm for the merge rules.
    const snapshot = buildSnapshotFromForm(
      data,
      analysisSnapshotRef.current,
      companyLogoUrlRef.current
    );

    const request =
      applyInFlightRef.current ??
      applySnapshotAsync({
        snapshot,
        // Brand + hours + description are already baked into the org via
        // `createOrganizationAsync` — passing `ignore` avoids a duplicate
        // updateOrganizationSettings write with identical values.
        modes: {
          brand: 'ignore',
          hours: 'ignore',
          description: 'ignore',
        },
      });
    applyInFlightRef.current = request;
    const settle = () => {
      if (applyInFlightRef.current === request) applyInFlightRef.current = null;
    };
    request.then(settle, settle);

    // A rejection propagates to createOrganization, which reports it.
    const applyOutcome = await withTimeout(request, 60_000);
    if (!applyOutcome) {
      toast.error(
        'Saving your services and locations is taking longer than expected. Please try again in a moment.'
      );
      return false;
    }

    snapshotAppliedRef.current = true;

    // Show a per-section toast only when a section was expected to write
    // rows but wrote zero — mirrors the old "all failed" branches.
    const expectedServices = snapshot.services?.length ?? 0;
    const expectedLocations = snapshot.locations?.length ?? 0;
    // The owner is pre-filled as the first team member, but creating the org
    // already made them staff: the writer matches that row and creates
    // nothing. Counting them made every onboarding report "failed to save
    // team members" for a team that was complete.
    const ownerEmail = user?.email?.toLowerCase();
    const expectedPractitioners = (snapshot.practitioners ?? []).filter(
      (p) => !ownerEmail || p.email?.toLowerCase() !== ownerEmail
    ).length;
    if (expectedServices > 0 && applyOutcome.createdServiceIds.length === 0) {
      toast.error(
        'Failed to save services. You can add them from the dashboard.'
      );
    }
    if (expectedLocations > 0 && applyOutcome.locationsCreated === 0) {
      toast.error(
        'Failed to save locations. You can add them from the dashboard.'
      );
    }
    if (expectedPractitioners > 0 && applyOutcome.practitionersCreated === 0) {
      toast.error(
        'Failed to save team members. You can add them from the dashboard.'
      );
    }

    // ── Fire-and-forget: invitations ─────────────────────────────────────
    for (const p of data.practitioners ?? []) {
      if (!p.email || p.email.toLowerCase() === user?.email?.toLowerCase())
        continue;
      apiClient
        .post(`organizations/${organizationId}/invitations`, {
          email: p.email,
          role: 'member',
        })
        .catch((err) => console.error(`Failed to invite ${p.email}:`, err));
    }

    return true;
  };

  // Base steps (1-9)
  const baseSteps: (StepConfig<OnboardingFormData> & { _id: string })[] = [
    {
      _id: 'business-name',
      id: 'business-name',
      schema: step1Schema,
      component: (form) => {
        formDataRef.current = form.getValues();
        return <Step1BusinessInfo form={form} />;
      },
    },
    {
      _id: 'business-type',
      id: 'business-type',
      schema: step1bSchema,
      component: (form) => {
        formDataRef.current = form.getValues();
        return <Step2BusinessType form={form} />;
      },
    },
    {
      _id: 'website-scanner',
      id: 'website-scanner',
      schema: step2Schema,
      processingButtonText: 'Analysing...',
      onBeforeContinue: async (form) => {
        const websiteUrl = form.getValues('websiteUrl');
        const bookingSystemUrl = form.getValues('bookingSystemUrl');

        // Skip analysis if no URLs provided
        if (!websiteUrl && !bookingSystemUrl) return true;

        // Skip analysis if the operator ALREADY uploaded a snapshot — the
        // upload populated the URL fields as a side effect, and re-running
        // the scanner would waste minutes producing data we already have.
        if (analysisSnapshotRef.current) return true;

        try {
          const result = await analyzeWebsiteAsync({
            // At least one URL is guaranteed truthy by the guard above
            websiteUrl: (websiteUrl || bookingSystemUrl) as string,
            facebookPageUrl: form.getValues('facebookPageUrl') || undefined,
            bookingSystemUrl: bookingSystemUrl || undefined,
          });

          // The scanner and the operator-upload path both feed this same
          // mapping — see apply-snapshot-to-form.ts. When they diverge, this
          // is the ONLY place that has to change.
          applySnapshotToForm(form, result);
          // Stash the scanner's response so createOrganization can flow
          // packages/variants/memberships through apply-snapshot at submit.
          analysisSnapshotRef.current = result as WebsiteAnalysisSnapshot;
          return true;
        } catch {
          toast.error(
            "We couldn't analyze your website/booking system. You can fill in the details manually."
          );
          return true;
        }
      },
      component: (form) => {
        formDataRef.current = form.getValues();
        return (
          <Step3WebsiteAnalysis
            form={form}
            isAnalyzing={isAnalyzing}
            onSnapshotUploaded={(result) => {
              applySnapshotToForm(form, result);
              // Same reason as the scanner branch above: keep the raw
              // snapshot so submit can hand its packages/variants/memberships
              // to the writer.
              analysisSnapshotRef.current = result;
            }}
          />
        );
      },
    },
    {
      _id: 'services',
      id: 'services',
      // Services are optional when the owner skips website analysis. When a
      // scan did run, this is the review/edit step for the categorized result.
      schema: z.object({}),
      component: (form) => {
        formDataRef.current = form.getValues();
        return <Step5Services form={form} />;
      },
    },
    {
      _id: 'locations',
      id: 'locations',
      schema: step6Schema_locations,
      component: (form) => {
        formDataRef.current = form.getValues();
        return <Step6Locations form={form} />;
      },
    },
    {
      _id: 'practitioners',
      id: 'practitioners',
      schema: stepPractitionersSchema,
      component: (form) => {
        formDataRef.current = form.getValues();
        return <StepPractitioners form={form} />;
      },
    },
    {
      _id: 'booking-system-select',
      id: 'booking-system-select',
      schema: bookingLinksStepSchema,
      component: (form) => {
        formDataRef.current = form.getValues();
        return <StepBookingLinks form={form} />;
      },
    },
    // TODO: Re-enable deposit system selection when integrations are built
    // {
    //   _id: 'deposit-system-select',
    //   id: 'deposit-system-select',
    //   schema: depositSystemSelectStepSchema,
    //   onBeforeContinue: async (form) => {
    //     const choice = form.getValues('depositSystemChoice');
    //     setDepositChoice(choice || 'none');
    //     return true;
    //   },
    //   component: (form) => {
    //     formDataRef.current = form.getValues();
    //     return <StepDepositSystemSelect form={form} />;
    //   },
    // },
    {
      _id: 'opening-hours',
      id: 'opening-hours',
      schema: step4Schema,
      component: (form) => {
        formDataRef.current = form.getValues();
        return <Step7OpeningHours form={form} />;
      },
    },
    {
      _id: 'credibility',
      id: 'credibility',
      schema: step5Schema,
      processingButtonText: 'Setting up...',
      onBeforeContinue: createOrganization,
      component: (form) => {
        formDataRef.current = form.getValues();
        return <Step8Credibility form={form} />;
      },
    },
    // TODO: Re-enable stripe-connect step when deposits are built
    // ...(depositChoice === 'stripe'
    //   ? [
    //       {
    //         _id: 'stripe-connect',
    //         id: 'stripe-connect',
    //         schema: stripeConnectStepSchema,
    //         component: (form: UseFormReturn<OnboardingFormData>) => {
    //           formDataRef.current = form.getValues();
    //           return (
    //             <Step6StripeConnect
    //               form={form}
    //               onConnectStripe={() => {
    //                 saveFormState(form);
    //                 initiateStripeConnect();
    //               }}
    //               isConnectingStripe={isConnectingStripe}
    //             />
    //           );
    //         },
    //         onBeforeContinue: async (form: UseFormReturn<OnboardingFormData>) => {
    //           const isConnected = form.getValues('stripeConnected');
    //           setStripeConnected(!!isConnected);
    //           return true;
    //         },
    //       },
    //     ]
    //   : []),
  ];

  // TODO: Re-enable deposits + calendar conditional steps when built
  const conditionalSteps: (StepConfig<OnboardingFormData> & {
    _id: string;
  })[] = [];

  // if (stripeConnected) {
  //   conditionalSteps.push({
  //     _id: 'deposits',
  //     id: 'deposits',
  //     schema: depositsStepSchema,
  //     onBeforeContinue: async (form) => {
  //       const details = form.getValues('serviceDetails') || {};
  //       const services = form.getValues('services') || [];
  //       const updates = services
  //         .filter((svc) => details[svc]?.requiresDeposit)
  //         .map((svc) => ({
  //           name: svc,
  //           requiresDeposit: true,
  //           depositAmountCents: details[svc]?.depositAmountCents,
  //         }));
  //
  //       if (updates.length > 0) {
  //         try {
  //           const orgServices = await apiClient.get<{
  //             items: { id: string; name: string }[];
  //           }>('organization-services');
  //           for (const update of updates) {
  //             const existing = orgServices.items?.find(
  //               (s) => s.name === update.name
  //             );
  //             if (existing) {
  //               await apiClient.put(`organization-services/${existing.id}`, {
  //                 requiresDeposit: update.requiresDeposit,
  //                 depositAmountCents: update.depositAmountCents,
  //               });
  //             }
  //           }
  //         } catch (err) {
  //           console.error('Failed to save deposit settings:', err);
  //         }
  //       }
  //       return true;
  //     },
  //     component: (form: UseFormReturn<OnboardingFormData>) => {
  //       formDataRef.current = form.getValues();
  //       return <Step7Deposits form={form} />;
  //     },
  //   });
  // }

  conditionalSteps.push({
    _id: 'owner-provides-services',
    id: 'owner-provides-services',
    schema: ownerProvidesServicesSchema,
    component: (form: UseFormReturn<OnboardingFormData>) => {
      formDataRef.current = form.getValues();
      return <StepDoYouProvideServices form={form} />;
    },
  });

  const allSteps: (StepConfig<OnboardingFormData> & { _id: string })[] = [
    ...baseSteps,
    ...conditionalSteps,
  ];

  const steps: StepConfig<OnboardingFormData>[] = allSteps;

  // Seed the signed-up user as the first team member
  const userAsPractitioner = user
    ? {
        id: user.id,
        name: user.name || '',
        email: user.email || '',
        title: '',
      }
    : null;

  // Compute default values (merge with restored OAuth state if returning)
  const defaultValues: OnboardingFormData = oauthReturn
    ? {
        ...BASE_DEFAULT_VALUES,
        ...(oauthReturn.formValues as Partial<OnboardingFormData>),
        companyLogo: null, // File objects can't be restored from sessionStorage
        // Mark Stripe as connected if returning from Stripe OAuth
        ...(oauthReturn.source === 'stripe' && { stripeConnected: true }),
      }
    : {
        ...BASE_DEFAULT_VALUES,
        practitioners: userAsPractitioner ? [userAsPractitioner] : [],
      };

  // Compute initial step for OAuth returns
  const getInitialStep = () => {
    if (!oauthReturn) return undefined;
    // Find the step index by _id based on what OAuth flow we returned from
    const targetId = 'stripe-connect';
    const idx = allSteps.findIndex((s) => s._id === targetId);
    return idx >= 0 ? idx : baseSteps.length;
  };
  const initialStep = getInitialStep();

  const handleSubmit = async (data: OnboardingFormData) => {
    // Org was already created in onBeforeContinue on the credibility step
    hasCompletedOnboardingRef.current = true;

    // Services are already created above with whatever the website scan found.
    // Missing prices are filled in later from Catalog → Services rather than in
    // a blocking funnel step.
    //
    // Finish INTO THE PRODUCT, not into a pricing page. Customers arrive here
    // having already bought on a sales call — an onboarding specialist attaches
    // the Stripe subscription they paid for — so a plan-selection screen at the
    // end asks them to buy something they own, and does it at the moment they
    // were promised they were finished.
    //
    // /billing stays reachable from the sidebar for plan and credit
    // management, and the paid-plan guards on the API still hold, so nothing
    // is given away by not asking here.
    void navigate({
      to: data.ownerProvidesServices ? '/setup-profile' : routes.home,
    });
  };

  return (
    <MultiStepForm
      steps={steps}
      defaultValues={defaultValues}
      fullSchema={onboardingSchema}
      onSubmit={handleSubmit}
      submitButtonText="Complete Setup"
      continueButtonText="Continue"
      initialStep={initialStep}
    />
  );
}
